import { useEffect, useState } from "react";
import api from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import { exportCsv } from "@/lib/csv";
import { ownerColor } from "@/lib/palette";
import { Activity as ActivityIcon, FileDown, Filter, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isOwner } from "@/lib/roles";

const ACTION_LABEL = {
  created: "Created",
  stage_changed: "Stage changed",
  blocked: "Blocked",
  unblocked: "Unblocked",
  archived: "Archived",
  restored: "Restored",
  task_toggled: "Task toggled",
  task_added: "Task added",
  task_updated: "Task updated",
  task_removed: "Task removed",
  owner_changed: "Owner changed",
  next_action_set: "Next action set",
};

const ACTION_COLOR = {
  created: "#2563EB",
  stage_changed: "#16A34A",
  blocked: "#7e22ce",
  unblocked: "#4F46E5",
  archived: "#b91c1c",
  restored: "#FBBF24",
  task_toggled: "#0D9488",
  task_added: "#0891B2",
  task_updated: "#0891B2",
  task_removed: "#A4647A",
  owner_changed: "#EA580C",
  next_action_set: "#FBBF24",
};

export default function ActivityPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [phiLog, setPhiLog] = useState(null);
  useEffect(() => {
    if (isOwner(user)) {
      api.get("/audit/phi-access", { params: { days: 30 } }).then(({ data }) => setPhiLog(data));
    }
  }, [user]);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [limit, setLimit] = useState(200);

  const load = () => {
    const params = { limit };
    if (userFilter) params.user_id = userFilter;
    if (actionFilter) params.action = actionFilter;
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    api.get("/activity", { params }).then(({ data }) => setEvents(data));
  };

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data));
  }, []);
  useEffect(load, [userFilter, actionFilter, from, to, limit]);

  const exportAuditCsv = () => {
    exportCsv(
      `avida-audit-${new Date().toISOString().slice(0, 10)}.csv`,
      events.map((e) => ({
        timestamp: e.timestamp,
        user: e.user_name,
        action: e.action,
        details: e.details,
        client: e.card_full_name || "",
        av_id: e.card_av_id || "",
        stage: e.card_stage || "",
        status: e.card_status || "",
      }))
    );
  };

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl w-full mx-auto">
        {phiLog && (
          <section data-testid="phi-access-audit" className="bg-white border border-[#15233B]/10 rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="px-5 py-3 border-b border-[#15233B]/10 bg-[#b91c1c] text-white flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-[#FFCB05]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFCB05]">HIPAA · PHI access audit (last 30 days)</div>
                <h3 className="text-sm font-extrabold tracking-tight">Field agent client views</h3>
              </div>
              <span className="ml-auto text-xs font-bold">
                {phiLog.count} view{phiLog.count === 1 ? "" : "s"}
              </span>
            </div>
            <div className="p-4">
              {phiLog.summary.length === 0 ? (
                <div className="text-xs italic text-[#15233B]/40 text-center py-4">No PHI access by field agents yet.</div>
              ) : (
                <table className="w-full text-xs mb-3">
                  <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60 border-b border-[#15233B]/10">
                    <tr>
                      <th className="text-left py-2 pr-2">Field agent</th>
                      <th className="text-right py-2 px-2">Views</th>
                      <th className="text-right py-2 pl-2">Distinct clients</th>
                    </tr>
                  </thead>
                  <tbody>
                    {phiLog.summary.map((s) => (
                      <tr key={s.user_id} data-testid={`phi-audit-${s.user_id}`} className="border-b border-[#15233B]/8">
                        <td className="py-2 pr-2 font-bold text-[#15233B]">{s.name} <span className="text-[10px] text-[#15233B]/45 font-mono ml-1">{s.email}</span></td>
                        <td className="py-2 px-2 text-right tabular-nums font-extrabold text-[#15233B]">{s.views}</td>
                        <td className="py-2 pl-2 text-right tabular-nums">{s.distinct_cards}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-[#15233B]/60 font-semibold hover:text-[#15233B]">View last 200 access records</summary>
                <div className="mt-2 max-h-72 overflow-y-auto border border-[#15233B]/10 rounded">
                  <table className="w-full text-[11px]">
                    <thead className="bg-[#F2F2F2] sticky top-0 text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/55">
                      <tr><th className="text-left px-2 py-1.5">Timestamp</th><th className="text-left px-2 py-1.5">Agent</th><th className="text-left px-2 py-1.5">Client</th></tr>
                    </thead>
                    <tbody>
                      {phiLog.rows.map((r) => (
                        <tr key={r.id} className="border-t border-[#15233B]/8">
                          <td className="px-2 py-1 font-mono text-[#15233B]/70">{new Date(r.timestamp).toLocaleString()}</td>
                          <td className="px-2 py-1">{r.user_name}</td>
                          <td className="px-2 py-1 truncate">{r.card_full_name} <span className="font-mono text-[#15233B]/45">{r.card_av_id}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          </section>
        )}

        <div className="flex items-center justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 text-[#FBBF24]">
              <ActivityIcon className="h-5 w-5" />
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold">Activity feed · Audit log</span>
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">All activity</h1>
            <p className="text-sm text-[#15233B]/60">Every stage move, block, task and ownership change across the agency.</p>
          </div>
          <button
            onClick={exportAuditCsv}
            data-testid="audit-csv-export"
            className="px-3 py-2 rounded-md border border-[#15233B]/15 hover:bg-[#15233B]/5 text-xs font-semibold flex items-center gap-1"
          >
            <FileDown className="h-3.5 w-3.5" /> Export audit log
          </button>
        </div>

        <div className="bg-white border border-[#15233B]/10 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-bold uppercase tracking-[0.2em] text-[#15233B]/60 flex items-center gap-1">
            <Filter className="h-3 w-3" /> Filters
          </span>
          <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} data-testid="activity-user-filter" className="border border-[#15233B]/15 rounded px-2 py-1.5 bg-white font-semibold">
            <option value="">All users</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} data-testid="activity-action-filter" className="border border-[#15233B]/15 rounded px-2 py-1.5 bg-white font-semibold">
            <option value="">All actions</option>
            {Object.entries(ACTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="activity-from" className="border border-[#15233B]/15 rounded px-2 py-1.5 bg-white" placeholder="from" />
          <span className="text-[#15233B]/40">to</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="activity-to" className="border border-[#15233B]/15 rounded px-2 py-1.5 bg-white" placeholder="to" />
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="border border-[#15233B]/15 rounded px-2 py-1.5 bg-white font-semibold">
            {[100, 200, 500, 1000].map((n) => <option key={n} value={n}>Last {n}</option>)}
          </select>
          <span className="ml-auto font-bold text-[#15233B]/60">{events.length} events</span>
        </div>

        <div className="bg-white border border-[#15233B]/10 rounded-md shadow-sm overflow-hidden">
          <ol className="divide-y divide-[#15233B]/8">
            {events.map((e) => {
              const oc = ownerColor(e.user_id);
              const ac = ACTION_COLOR[e.action] || "#15233B";
              return (
                <li key={e.id} className="px-5 py-3 flex items-start gap-3" data-testid={`activity-${e.id}`}>
                  <span
                    className="h-7 w-7 rounded-full grid place-items-center text-[10px] font-bold shadow-sm shrink-0"
                    style={{ backgroundColor: oc.primary, color: oc.text }}
                  >
                    {(e.user_name || "?").slice(0, 1)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-[#15233B] text-sm">{e.user_name}</span>
                      <span
                        className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded"
                        style={{ backgroundColor: `${ac}1A`, color: ac }}
                      >
                        {ACTION_LABEL[e.action] || e.action}
                      </span>
                      {e.card_av_id && (
                        <span className="text-xs text-[#15233B]/70 font-mono">{e.card_av_id}</span>
                      )}
                      {e.card_full_name && (
                        <span className="text-xs text-[#15233B]/70">· {e.card_full_name}</span>
                      )}
                    </div>
                    <div className="text-sm text-[#15233B]/80 mt-0.5">{e.details}</div>
                    <div className="text-[10px] text-[#15233B]/40 mt-0.5">
                      {new Date(e.timestamp).toLocaleString()}
                    </div>
                  </div>
                </li>
              );
            })}
            {events.length === 0 && (
              <li className="px-5 py-10 text-xs italic text-[#15233B]/40 text-center">No events match these filters.</li>
            )}
          </ol>
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}
